<script setup lang="ts">
import * as Sentry from '@sentry/browser'
import UIKit from '@pocketprep/ui-kit'
import { isSupportWindow, isValidEmail } from '@/utils'
import { buildUTMParamString } from '@/store/utils'
import { giftModule } from '@/store/gift/module'
import type { TGift } from '@/store/gift/state'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const {
    Button: PocketButton,
    Errors,
    Icon,
    Input: PocketInput,
    Link: PocketLink,
} = UIKit

const route = useRoute()

const gift = ref<TGift | null>(null)
const isLoading = ref(true)
const hasGiftError = ref(false)
const recipientEmail = ref('')
const recipientEmailErrors = ref<string[]>([])
const hasSavedRecipient = ref(false)
const isSending = ref(false)

// When Stripe redirects the purchaser here after checkout, we'll have the checkout session_id query param
const checkoutSessionId = computed(() => {
    return typeof route.query.session_id === 'string' ? route.query.session_id : ''
})

onMounted(async () => {
    try {
        if (!checkoutSessionId.value) {
            throw new Error('GiftPurchased: no session_id in the URL')
        }
        // This will create or return the previously created gift if it already exists
        gift.value = await giftModule.actions.createGift({
            checkoutSessionId: checkoutSessionId.value,
        })
    } catch (err) {
        Sentry.captureException(err)
        hasGiftError.value = true
    }

    isLoading.value = false
})

const sendBtnClicked = async () => {
    const purchasedGift = gift.value
    if (!purchasedGift) {
        Sentry.captureException(new Error('GiftPurchased: No gift to update'))
        return
    }

    recipientEmailErrors.value = []

    if (!isValidEmail(recipientEmail.value)) {
        recipientEmailErrors.value.push('Invalid email address.')
        return
    }

    // Prevent duplicate submissions
    if (isSending.value) {
        return
    }

    isSending.value = true
    try {
        gift.value = await giftModule.actions.updateGift({
            giftId: purchasedGift.id,
            checkoutSessionId: checkoutSessionId.value,
            invitedEmail: recipientEmail.value,
        })
        hasSavedRecipient.value = true
    } catch (err) {
        Sentry.captureException(err)
        recipientEmailErrors.value.push(
            'We couldn\'t send that email. Try again, or contact support if the issue persists.'
        )
    }
    isSending.value = false
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}
</script>

<template>
    <div class="gift-purchased">
        <div class="gift-purchased__head">
            <PocketLink
                class="gift-purchased__logo-link"
                :href="`https://www.pocketprep.com/?${
                    buildUTMParamString({
                        utm_campaign: 'app_settings',
                        utm_content: 'gift_purchased_logo',
                    })
                }`"
                @mousedown.prevent
            >
                <img
                    class="gift-purchased__logo"
                    src="@/assets/logos/logo.svg"
                    alt="Pocket Prep Home"
                >
            </PocketLink>
            <PocketLink
                class="gift-purchased__help-link"
                @click="showIntercom"
                @keypress.enter="showIntercom"
                @mousedown.prevent
            >
                Help
            </PocketLink>
        </div>
        <main class="gift-purchased__content">
            <Icon
                v-if="isLoading"
                class="gift-purchased__loading"
                type="loading2"
            />
            <div v-else-if="hasGiftError" class="gift-purchased__text">
                <h1 class="gift-purchased__title">
                    We couldn't find <br>this gift
                </h1>
                <p class="gift-purchased__info">
                    Try again, or contact support if the issue persists.
                </p>
            </div>
            <template v-else>
                <div class="gift-purchased__brain">
                    <img
                        src="@/assets/brain/brain-heel-click.png"
                        alt="Cartoon-style brain with glasses jumping and clicking heels together"
                    >
                </div>
                <div class="gift-purchased__text">
                    <h1 class="gift-purchased__title">
                        Thank you for gifting <br>Pocket Prep!
                    </h1>
                    <p class="gift-purchased__info">
                        You'll receive an email with a gift link shortly. Forward it to your recipient
                        so they can redeem their gift (or use the box below).
                    </p>
                    <p class="gift-purchased__info">
                        There's no rush - their Premium access won't start until they activate the link.
                    </p>
                </div>
                <div class="gift-purchased__send">
                    <div class="gift-purchased__send-field">
                        <PocketInput
                            v-model="recipientEmail"
                            class="gift-purchased__send-input"
                            label="Recipient Email"
                            field-type="email"
                            :disabled="hasSavedRecipient"
                            :error="!!recipientEmailErrors.length"
                            @keydown.enter="sendBtnClicked"
                        />
                        <PocketButton
                            class="gift-purchased__send-btn"
                            :disabled="!recipientEmail || hasSavedRecipient"
                            :is-loading="isSending"
                            @click="sendBtnClicked"
                        >
                            {{ hasSavedRecipient ? 'Sent' : 'Send' }}
                        </PocketButton>
                    </div>
                    <Errors
                        v-if="recipientEmailErrors.length"
                        class="gift-purchased__send-errors"
                        :errors="recipientEmailErrors"
                    />
                </div>
                <PocketLink
                    class="gift-purchased__home-link"
                    :href="`https://www.pocketprep.com/?${
                        buildUTMParamString({
                            utm_campaign: 'app_settings',
                            utm_content: 'gift_purchased_home',
                        })
                    }`"
                    @mousedown.prevent
                >
                    Pocket Prep Home
                </PocketLink>
            </template>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.gift-purchased {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;

    &__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        row-gap: 8px;
        justify-content: space-between;
        width: 100%;
        min-height: 56px;
        padding: 8px 20px 8px 62px;
        box-sizing: border-box;
        border-bottom: 1px solid rgba($pewter, 0.3);
        background-color: $white;

        // Figma changes the left padding from 62px to 20px at 1440 breakpoint
        @include breakpoint(polar-bear) {
            padding: 8px 20px;
        }
    }

    &__logo-link {
        display: flex;
        max-width: 100%;
    }

    &__logo {
        display: block;
        height: 32px;
    }

    &__help-link {
        line-height: 20px;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 40px;
        flex: 1;
        width: 100%;
        padding-top: 64px;
        box-sizing: border-box;
        background-color: $gray-background;

        @include breakpoint(black-bear) {
            padding: 64px 18px 0;
        }
    }

    &__loading {
        margin-top: 100px;
    }

    &__brain {
        position: relative;
        width: 173px;
        height: 173px;

        // Letting the image overflow out of its container matches Figma
        // while keeping the layout spacing simpler
        img {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 173px;
            height: 203px;
        }
    }

    &__text {
        width: 100%;
        max-width: 400px;
        padding: 0 12px;
        box-sizing: border-box;
    }

    &__title {
        font-size: 26px;
        line-height: 36px;
        font-weight: 700;
        color: $brand-black;
        text-align: center;
        margin: 0 0 20px;
    }

    &__info {
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        color: $brand-black;
        margin: 0;

        &:last-child {
            margin-top: 22px;
        }
    }

    &__send {
        width: 100%;
        max-width: 402px;
    }

    &__send-field {
        display: flex;
        align-items: flex-end;
        gap: 10px;
    }

    &__send-input {
        flex: 1;
    }

    &__send-btn {
        height: 36px;
        padding: 8px 18px;
        line-height: 20px;
    }

    &__send-errors {
        margin-top: 8px;
    }

    &__home-link {
        padding: 8px 14px;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
    }
}
</style>
